import type { FloorEdgeOverridesTable } from "building-types/floors/floorAssemblyTypes";
import type { Table } from "common/data/table";
import { WindingDir } from "geom/core";
import type { DCEL, Polygon } from "geom/polyclip/dcel";
import { dcelFromPolygons } from "geom/polyclip/dcelFromPolygons";
import { SNAP_ROUNDING_TOLERANCE } from "geom/polyclip/polyClip";
import { polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import { union } from "geom/polyclip/setOps";
import {
  edgeIdsByPolyLine2,
  polyLine2Close,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2MergeCoincidentVerticesExtended,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { mirrorFloorOpening } from "../elements/floor/floorMirrorUtils";

export interface FloorEdgeProvenanceEntry {
  readonly sourcePolygonIndex: number;
  readonly sourceEdgeIndex: number;
}

/**
 * Runs polygon union on room outlines using the DCEL and extracts per-edge provenance
 * via the DCEL's segment-to-polygon tracking. Each output perimeter edge is traced back
 * to its source polygon (room index) and edge index within that polygon.
 *
 * Sub-edges (created when DCEL splits an original edge at intersection points) inherit
 * the same provenance, so composite `roomId:edgeId` keys are naturally duplicated for
 * all fragments of a shared room edge.
 */
export function roomOutlinesToUnionPerimetersWithProvenance(roomOutlines: PolyLine2[]): {
  perimeters: PolyLine2[];
  perimeterProvenance: FloorEdgeProvenanceEntry[][];
} {
  if (roomOutlines.length === 0) {
    return { perimeters: [], perimeterProvenance: [] };
  }

  const closed = roomOutlines.map((pl) => polyLine2Close(pl));
  const polygons: Polygon[] = closed.map((pl) => [polyLine2ToPolyClipPolygonLoop(pl, false, true, WindingDir.CW)]);

  const dcel = dcelFromPolygons({
    polygons,
    snapRoundPrecision: SNAP_ROUNDING_TOLERANCE,
  });

  const halfEdgeToSegment = new Map<number, number>();
  for (let s = 0; s < dcel.segmentToHalfEdges.length; s++) {
    for (const he of dcel.segmentToHalfEdges[s]) {
      if (!halfEdgeToSegment.has(he)) {
        halfEdgeToSegment.set(he, s);
      }
    }
  }

  const operands = polygons.map((_, i) => i);
  const capturedLoops: number[][] = [];

  union(dcel, operands, (_dcel, loops) => {
    capturedLoops.push(...loops);
    return loops;
  });

  const perimeters: PolyLine2[] = [];
  const perimeterProvenance: FloorEdgeProvenanceEntry[][] = [];

  for (const loop of capturedLoops) {
    const result = extractPerimeterFromLoop(dcel, loop, halfEdgeToSegment);
    if (result) {
      perimeters.push(result.perimeter);
      perimeterProvenance.push(result.provenance);
    }
  }

  return { perimeters, perimeterProvenance };
}

function extractPerimeterFromLoop(
  dcel: DCEL,
  loop: number[],
  halfEdgeToSegment: Map<number, number>,
): { perimeter: PolyLine2; provenance: FloorEdgeProvenanceEntry[] } | undefined {
  const n = loop.length;
  if (n < 3) {
    return undefined;
  }

  const rawProv: FloorEdgeProvenanceEntry[] = [];
  const pl = polyLine2New((n + 1) * 2);

  for (let i = 0; i < n; i++) {
    const he = loop[i];
    const startVertex = dcel.halfEdges[he][0];
    const [x, y] = dcel.vertices[startVertex];
    pl[i * 2] = x;
    pl[i * 2 + 1] = y;

    const segIdx = halfEdgeToSegment.get(he);
    if (segIdx !== undefined) {
      rawProv.push({
        sourcePolygonIndex: dcel.segmentToPolygon[segIdx],
        sourceEdgeIndex: dcel.segmentToPolygonLoopEdge[segIdx],
      });
    } else {
      rawProv.push({ sourcePolygonIndex: -1, sourceEdgeIndex: -1 });
    }
  }

  pl[n * 2] = pl[0];
  pl[n * 2 + 1] = pl[1];

  const winding = polyLine2WindingDir(pl);

  // The DCEL union outputs outer boundaries as CCW and inner holes as CW.
  // Only outer boundaries should be treated as floor perimeters; inner holes
  // (gaps left by excluded opening rooms) are handled via the openings list.
  if (winding === WindingDir.CW) {
    return undefined;
  }

  const prov = rawProv;

  const [merged, edgeMapping] = polyLine2MergeCoincidentVerticesExtended(pl);

  const mergedProv: FloorEdgeProvenanceEntry[] = [];
  for (let i = 0; i < edgeMapping.length; i++) {
    if (edgeMapping[i] !== -1) {
      mergedProv.push(prov[i]);
    }
  }

  if (merged.length < 6) {
    return undefined;
  }

  const finalPl = polyLine2Copy(merged);
  const needsReverse = polyLine2WindingDir(finalPl) === WindingDir.CW;
  polyLine2SetCCW(finalPl);
  const finalProv = needsReverse ? mergedProv.slice().reverse() : mergedProv;

  return { perimeter: finalPl, provenance: finalProv };
}

export function openingsToUse(
  openings: PolyLine2[] | undefined,
  mirror: boolean,
  mirrorTranslationX: number,
): PolyLine2[] {
  if (!openings) {
    return [];
  }
  return openings.map((opening) =>
    mirror
      ? mirrorFloorOpening(polyLine2SetCW(polyLine2Copy(opening)), mirrorTranslationX)
      : polyLine2SetCW(polyLine2Copy(opening)),
  );
}

/** Generate combined edgeIds and edgeCounts from an array of polylines. */
export function buildCombinedEdgeIds(polylines: PolyLine2[]): { edgeIds: string[]; edgeCounts: number[] } {
  const edgeIds: string[] = [];
  const edgeCounts: number[] = [];
  for (const pl of polylines) {
    const ids = edgeIdsByPolyLine2(pl);
    edgeIds.push(...ids);
    edgeCounts.push(ids.length);
  }
  return { edgeIds, edgeCounts };
}

interface RoomWithEdgeIds {
  readonly edgeIds?: string[];
}

export function buildOpeningEdgeIdsFromRooms(
  openings: PolyLine2[],
  openingRoomIds: string[],
  rooms: Table<RoomWithEdgeIds>,
): { edgeIds: string[]; edgeCounts: number[] } {
  const edgeIds: string[] = [];
  const edgeCounts: number[] = [];
  for (let i = 0; i < openings.length; i++) {
    const roomId = openingRoomIds[i];
    const room = rooms[roomId];
    const edgeCount = polyLine2EdgeCount(openings[i]);
    const roomEdgeIds = room?.edgeIds ?? [];
    const cwEdgeIds = roomEdgeIds.slice().reverse();
    for (let j = 0; j < edgeCount; j++) {
      edgeIds.push(`${roomId}:${cwEdgeIds[j] ?? String(j)}`);
    }
    edgeCounts.push(edgeCount);
  }
  return { edgeIds, edgeCounts };
}

/**
 * Return a copy of `overrides` containing only keys present in `edgeIds`.
 * Orphaned override keys (referencing consumed or deleted edges) are dropped.
 */
export function filterOverridesByEdgeIds(
  overrides: FloorEdgeOverridesTable | undefined,
  edgeIds: string[] | undefined,
): FloorEdgeOverridesTable | undefined {
  if (!overrides || !edgeIds || edgeIds.length === 0) {
    return undefined;
  }
  const validKeys = new Set(edgeIds);
  let filtered: Record<string, FloorEdgeOverridesTable[string]> | undefined;
  for (const key of Object.keys(overrides)) {
    if (validKeys.has(key)) {
      if (!filtered) {
        filtered = {};
      }
      filtered[key] = overrides[key];
    }
  }
  return filtered as FloorEdgeOverridesTable | undefined;
}
